import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export type IconProps = {
	className?: string;
	title?: string;
};

function IconBase({
	className,
	children,
	title,
}: IconProps & { children: ReactNode }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.75}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={cn("h-4 w-4 shrink-0", className)}
			aria-hidden={title ? undefined : true}
			role={title ? "img" : undefined}
		>
			{title ? <title>{title}</title> : null}
			{children}
		</svg>
	);
}

export function IconEye(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
			<circle cx="12" cy="12" r="3" />
		</IconBase>
	);
}

export function IconEyeOff(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M3 3l18 18" />
			<path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
			<path d="M9.9 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17.4 17.4 0 0 1-2.2 3.2" />
			<path d="M6.1 6.1C3.7 7.8 2 12 2 12s3.5 7 10 7a10.3 10.3 0 0 0 4.2-.9" />
		</IconBase>
	);
}

export function IconPencil(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
		</IconBase>
	);
}

export function IconTrash(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M3 6h18" />
			<path d="M8 6V4h8v2" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
			<path d="M10 11v6" />
			<path d="M14 11v6" />
		</IconBase>
	);
}

export function IconMoreHorizontal(props: IconProps) {
	return (
		<IconBase {...props}>
			<circle cx="5" cy="12" r="1.25" fill="currentColor" stroke="none" />
			<circle cx="12" cy="12" r="1.25" fill="currentColor" stroke="none" />
			<circle cx="19" cy="12" r="1.25" fill="currentColor" stroke="none" />
		</IconBase>
	);
}

export function IconPlus(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M12 5v14" />
			<path d="M5 12h14" />
		</IconBase>
	);
}

export function IconUpload(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
			<path d="M17 8l-5-5-5 5" />
			<path d="M12 3v12" />
		</IconBase>
	);
}

export function IconSearch(props: IconProps) {
	return (
		<IconBase {...props}>
			<circle cx="11" cy="11" r="7" />
			<path d="M20 20l-3.5-3.5" />
		</IconBase>
	);
}

export function IconShare(props: IconProps) {
	return (
		<IconBase {...props}>
			<circle cx="18" cy="5" r="2.5" />
			<circle cx="6" cy="12" r="2.5" />
			<circle cx="18" cy="19" r="2.5" />
			<path d="M8.3 13.2 15.7 17" />
			<path d="M15.7 7l-7.4 3.8" />
		</IconBase>
	);
}

export function IconChevronLeft(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M15 6 9 12l6 6" />
		</IconBase>
	);
}

export function IconChevronDown(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M6 9l6 6 6-6" />
		</IconBase>
	);
}

export function IconX(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M6 6l12 12" />
			<path d="M18 6 6 18" />
		</IconBase>
	);
}

export function IconMapPin(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11Z" />
			<circle cx="12" cy="10" r="2.5" />
		</IconBase>
	);
}

export function IconLayers(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M12 3 2.5 8 12 13l9.5-5Z" />
			<path d="M2.5 14 12 19l9.5-5" />
			<path d="M2.5 11 12 16l9.5-5" />
		</IconBase>
	);
}

export function IconCursor(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M4 4 11 20l2.2-6.3L20 11Z" />
		</IconBase>
	);
}

export function IconLine(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M5 19 19 5" />
			<circle cx="5" cy="19" r="1.5" />
			<circle cx="19" cy="5" r="1.5" />
		</IconBase>
	);
}

export function IconPolygon(props: IconProps) {
	return (
		<IconBase {...props}>
			<path d="M7 4h10l4 8-4 8H7L3 12Z" />
		</IconBase>
	);
}
